import type { ProductDataDTO } from "~/domains/product/types/ProductDataDTO";
import type { CartViewEntryDTO, LineItemDTO } from "../dto/CartViewDTO";
import type {
  EntryGroup,
  KitGroup,
  PackageEntryDTO,
  PackPhaseoutGroup,
  ProductPhaseoutGroup,
  ProductGroup,
} from "../dto/CartResponseDTO";
import type { ConfiguratorProductData } from "~/domains/product/types/ProductData";
import { transformLinear } from "~/features/productDetails/transformers/ProductDataTransformer";
import { createLinearProductCode } from "~/utils/cartHelper";

/**
 * Fallback id for checkout page
 */
let fallbackPK = -1;

function getNextFallbackPK() {
  return fallbackPK--;
}

/**
 * Band size separating group categories in the in-memory group-number scheme.
 * Each category gets a dedicated band of 10,000 slots — far exceeding the
 * realistic maximum of 2,000 entries per category.
 */
const GROUP_NUMBER_BAND = 10_000;

/**
 * Group number scheme (in-memory only, never sent to BE):
 *
 * | Group                          | Number                                              |
 * | ------------------------------ | --------------------------------------------------- |
 * | ORDERABLE root                 | 1                                                   |
 * | QUOTABLE root                  | 2                                                   |
 * | PRODUCT groups (orderable)     | startProductGroupNumber + entryIndex                 |
 * | PRODUCT groups (quotable)      | startProductGroupNumber + entryIndex                 |
 * | PRODUCT_PHASEOUT (orderable)   | startProductGroupNumber + GROUP_NUMBER_BAND + phaseoutIndex   |
 * | PRODUCT_PHASEOUT (quotable)    | startProductGroupNumber + GROUP_NUMBER_BAND + phaseoutIndex   |
 * | PACK_PHASEOUT (orderable)      | startProductGroupNumber + 2*GROUP_NUMBER_BAND + packPhaseoutIndex |
 * | PACK_PHASEOUT (quotable)       | startProductGroupNumber + 2*GROUP_NUMBER_BAND + packPhaseoutIndex |
 * | KIT groups (orderable)         | startProductGroupNumber + 3*GROUP_NUMBER_BAND + kitIndex        |
 * | KIT groups (quotable)          | startProductGroupNumber + 3*GROUP_NUMBER_BAND + kitIndex        |
 *
 * For the standard call sites:
 *   - ORDERABLE: rootGroupNumber=1, startProductGroupNumber=100
 *     → PRODUCT 100–10,099, PRODUCT_PHASEOUT 10,100–20,099,
 *       PACK_PHASEOUT 20,100–30,099, KIT 30,100–40,099
 *   - QUOTABLE:  rootGroupNumber=2, startProductGroupNumber=50,000
 *     → PRODUCT 50,000–60,099, PRODUCT_PHASEOUT 60,000–70,099,
 *       PACK_PHASEOUT 70,000–80,099, KIT 80,000–90,099
 */
export function convertLineItemToPackageEntry(
  lineItem: LineItemDTO,
  productCode: string,
  groupNumbers: number[],
  orderEntryPk: number,
): PackageEntryDTO {
  return {
    pk: lineItem.pk ?? getNextFallbackPK(),
    orderEntryPk,
    entryNumber: lineItem.entryNumber,
    productCode,
    groupNumbers,
    quantity: lineItem.quantity,
    packageSize: lineItem.packageSize,
    packageCode: lineItem.packageCode,
    comment: lineItem.comment,
    selectedDeliveryDate: lineItem.selectedDeliveryDate,
    selectedDeliveryDateIsManual: lineItem.selectedDeliveryDateIsManual,
    primaryWarehouseName: lineItem.primaryWarehouseName ?? undefined,
    primaryWarehouseCode: lineItem.primaryWarehouseCode ?? undefined,
    netPrice: lineItem.netPrice ?? undefined,
    priceUnit: lineItem.priceUnit ?? undefined,
    priceReference: lineItem.priceReference ?? undefined,
    contractLine: lineItem.contractLine ?? undefined,
    contractNumber: lineItem.contractNumber ?? undefined,
    quoteLineNumber: lineItem.quoteLineNumber,
    quoteScheduleLineNum: lineItem.quoteScheduleLineNum,
    quoteNumber: lineItem.quoteNumber ?? undefined,
    quoteMinQuantity: lineItem.quoteMinQuantity ?? undefined,
    isBundled: lineItem.isBundled ?? undefined,
    original: lineItem.original ?? undefined,
    estimatedShippingDate: lineItem.estimatedShippingDate,
    logisticalComment: lineItem.logisticalComment,
    quoteExpiryDate: lineItem.quoteExpiryDate,
    isRestrictedPackage: lineItem.isRestrictedPackage ?? undefined,
    customerOrderNumber: lineItem.customerOrderNumber,
    originCountry: lineItem.originCountry,
    source: lineItem.source,
    pnaCall: lineItem.pnaCall,
    surcharge: lineItem.surcharge ?? undefined,
    isTariffSurchargeApplicable: lineItem.isTariffSurchargeApplicable,
    salesCampaignCode: lineItem.salesCampaignCode,
    specialPrice: lineItem.specialPrice,
    saleNetPrice: lineItem.saleNetPrice,
    actualNetPrice: lineItem.actualNetPrice,
    actualSalePrice: lineItem.actualSalePrice,
    recommendationPlace: lineItem.recommendationPlace,
    recommendationType: lineItem.recommendationType,
  };
}

/**
 * Group number scheme (in-memory only, never sent to BE):
 *
 * | Group                          | Number                                           |
 * | ------------------------------ | ------------------------------------------------ |
 * | ORDERABLE root                 | 1                                                |
 * | QUOTABLE root                  | 2                                                |
 * | PRODUCT groups (orderable)     | startProductGroupNumber + entryIndex              |
 * | PRODUCT groups (quotable)      | startProductGroupNumber + entryIndex              |
 * | PRODUCT_PHASEOUT (orderable)   | startProductGroupNumber + 500 + phaseoutIndex     |
 * | PRODUCT_PHASEOUT (quotable)    | startProductGroupNumber + 500 + phaseoutIndex     |
 * | PACK_PHASEOUT (orderable)      | startProductGroupNumber + 800 + packPhaseoutIndex |
 * | PACK_PHASEOUT (quotable)       | startProductGroupNumber + 800 + packPhaseoutIndex |
 * | KIT groups (orderable)         | startProductGroupNumber + 1000 + kitIndex         |
 * | KIT groups (quotable)          | startProductGroupNumber + 1000 + kitIndex         |
 *
 * For the standard call sites:
 *   - ORDERABLE: rootGroupNumber=1, startProductGroupNumber=100
 *     → PRODUCT 100+, PRODUCT_PHASEOUT 600+, PACK_PHASEOUT 900+, KIT 1100+
 *   - QUOTABLE:  rootGroupNumber=2, startProductGroupNumber=200
 *     → PRODUCT 200+, PRODUCT_PHASEOUT 700+, PACK_PHASEOUT 1000+, KIT 1200+
 */
export function buildEntryGroupsFromEntries(
  entries: CartViewEntryDTO[],
  groupType: "ORDERABLE" | "QUOTABLE",
  rootGroupNumber: number,
  startProductGroupNumber: number,
): {
  entryGroups: EntryGroup[];
  packageEntries: PackageEntryDTO[];
  products: ProductDataDTO[];
  linearProducts: ConfiguratorProductData[];
} {
  const kitOffset = 3 * GROUP_NUMBER_BAND;

  const rootGroup: EntryGroup = {
    groupNumber: rootGroupNumber,
    groupType,
    children: [],
  };

  const allPackageEntries: PackageEntryDTO[] = [];
  // Deduplicated by productCode
  const productsMap = new Map<string, ProductDataDTO>();
  const linearProductsMap = new Map<string, ConfiguratorProductData>();

  let kitIndex = 0;

  entries.forEach((entry, entryIndex) => {
    const productGroupNumber = startProductGroupNumber + entryIndex;

    // If the entry has linear info, use a synthetic product code and collect the
    // ConfiguratorProductData so it can be pre-seeded into the product store.
    const isLinear = entry.productLinearInfo != null;
    const productCode = isLinear ? createLinearProductCode(entry.product.code) : entry.product.code;

    if (isLinear) {
      if (!linearProductsMap.has(productCode)) {
        linearProductsMap.set(productCode, transformLinear(entry.product, entry.productLinearInfo!, productCode));
      }
    } else {
      productsMap.set(productCode, entry.product);
    }

    const productGroup: ProductGroup = {
      groupNumber: productGroupNumber,
      groupType: "PRODUCT",
      productCode,
      children: [],
    };

    for (const lineItem of entry.packageEntries) {
      const hasOptionalParts = lineItem.optionalParts != null && lineItem.optionalParts.length > 0;

      if (hasOptionalParts) {
        // KIT housing entry
        const kitGroupNumber = startProductGroupNumber + kitOffset + kitIndex;
        kitIndex++;

        const kitGroup: KitGroup = {
          groupNumber: kitGroupNumber,
          groupType: "KIT",
          housingEntryPk: lineItem.pk,
          children: [],
        };

        const housingGroupNumbers = [rootGroupNumber, productGroupNumber, kitGroupNumber];
        const housingEntry = convertLineItemToPackageEntry(lineItem, productCode, housingGroupNumbers, entry.pk);
        allPackageEntries.push(housingEntry);

        // Optional parts
        for (const part of lineItem.optionalParts!) {
          const partProductCode = part.product.code;
          productsMap.set(partProductCode, part.product);

          for (const partLineItem of part.packageEntries) {
            const partGroupNumbers = [rootGroupNumber, productGroupNumber, kitGroupNumber];
            const partEntry = convertLineItemToPackageEntry(partLineItem, partProductCode, partGroupNumbers, entry.pk);
            allPackageEntries.push(partEntry);
          }
        }

        productGroup.children.push(kitGroup);
      } else {
        // Non-KIT entry
        const groupNumbers = [rootGroupNumber, productGroupNumber];
        const packageEntry = convertLineItemToPackageEntry(lineItem, productCode, groupNumbers, entry.pk);
        allPackageEntries.push(packageEntry);
      }
    }

    rootGroup.children.push(productGroup);
  });

  // Second pass: synthesize PHASEOUT groups by deriving predecessorPackEntryPks from
  // FifteenDigitDTOEntry.predecessorPacks on each line item's product data.
  //
  // Step 1 — build a materialNo → LineItemDTO.pk[] lookup across all entries so that
  // predecessor 15-digit materialNos can be resolved to all PKs of the line items
  // currently in the cart that carry those packs (multiple line items may share the
  // same 15-digit materialNo when the same product+pack appears more than once).
  const materialNoToPks = new Map<string, number[]>();
  for (const entry of entries) {
    const fifteenDigit = entry.product.fifteenDigit ?? [];
    for (const lineItem of entry.packageEntries) {
      const dtoEntry = fifteenDigit.find((d) => d.materialNo.slice(-2) === lineItem.packageCode);
      if (dtoEntry) {
        const existing = materialNoToPks.get(dtoEntry.materialNo);
        if (existing) {
          existing.push(lineItem.pk);
        } else {
          materialNoToPks.set(dtoEntry.materialNo, [lineItem.pk]);
        }
      }
    }
  }

  // Build a reverse lookup: package-entry pk → productGroupNumber
  const pkToProductGroupNumber = new Map<number, number>();
  for (const pe of allPackageEntries) {
    // productGroupNumber is the second element in groupNumbers (index 1)
    const productGroupNumber = pe.groupNumbers[1];
    if (productGroupNumber !== undefined) {
      pkToProductGroupNumber.set(pe.pk, productGroupNumber);
    }
  }

  let phaseoutIndex = 0;
  let packPhaseoutIndex = 0;

  entries.forEach((entry, entryIndex) => {
    // Step 2 — for this entry, collect all predecessorPacks materialNos declared across
    // its line items' FifteenDigitDTOEntry records, then resolve them to PKs.
    const fifteenDigit = entry.product.fifteenDigit ?? [];
    const predecessorMaterialNos = new Set<string>();
    for (const lineItem of entry.packageEntries) {
      const dtoEntry = fifteenDigit.find((d) => d.materialNo.slice(-2) === lineItem.packageCode);
      for (const predecessorMaterialNo of dtoEntry?.predecessorPacks ?? []) {
        predecessorMaterialNos.add(predecessorMaterialNo);
      }
    }

    const pks = Array.from(predecessorMaterialNos).flatMap((materialNo) => materialNoToPks.get(materialNo) ?? []);

    if (pks.length === 0) {
      return;
    }

    const successorGroupNumber = startProductGroupNumber + entryIndex;

    // Partition resolved PKs into same-product and cross-product sets
    const crossProductPredecessorPks: number[] = [];
    const sameProductPredecessorPks: number[] = [];
    for (const pk of pks) {
      const pgn = pkToProductGroupNumber.get(pk);
      if (pgn === undefined) {
        continue;
      }
      if (pgn === successorGroupNumber) {
        sameProductPredecessorPks.push(pk);
      } else {
        crossProductPredecessorPks.push(pk);
      }
    }

    // Same-product predecessors → PACK_PHASEOUT child inside the successor's PRODUCT group
    if (sameProductPredecessorPks.length > 0) {
      const successorProductGroup = rootGroup.children.find(
        (child): child is ProductGroup => child.groupType === "PRODUCT" && child.groupNumber === successorGroupNumber,
      );
      if (successorProductGroup) {
        const sameProductPredecessorPkSet = new Set(sameProductPredecessorPks);
        const successorPackEntryPks = allPackageEntries
          .filter((pe) => pe.groupNumbers[1] === successorGroupNumber && !sameProductPredecessorPkSet.has(pe.pk))
          .map((pe) => pe.pk);

        const packPhaseoutGroup: PackPhaseoutGroup = {
          groupNumber: startProductGroupNumber + 2 * GROUP_NUMBER_BAND + packPhaseoutIndex++,
          groupType: "PACK_PHASEOUT",
          predecessorPackEntryPks: sameProductPredecessorPks,
          successorPackEntryPks,
          children: [],
        };
        successorProductGroup.children.push(packPhaseoutGroup);
      }
    }

    // Cross-product predecessors → PRODUCT_PHASEOUT group wrapping predecessor+successor PRODUCT groups
    const predecessorGroupNumberSet = new Set<number>();
    for (const pk of crossProductPredecessorPks) {
      const pgn = pkToProductGroupNumber.get(pk);
      if (pgn !== undefined) {
        predecessorGroupNumberSet.add(pgn);
      }
    }

    if (predecessorGroupNumberSet.size === 0) {
      return;
    }

    const predecessorGroupNumbers = Array.from(predecessorGroupNumberSet);

    // Collect the actual PRODUCT group objects before removing them so they
    // can be nested inside the PHASEOUT group as children.
    const groupNumbersToRemove = new Set([...predecessorGroupNumbers, successorGroupNumber]);
    const phaseoutChildren = rootGroup.children.filter((child) => groupNumbersToRemove.has(child.groupNumber));

    const phaseoutGroup: ProductPhaseoutGroup = {
      groupNumber: startProductGroupNumber + GROUP_NUMBER_BAND + phaseoutIndex++,
      groupType: "PRODUCT_PHASEOUT",
      predecessorGroupNumbers,
      successorGroupNumbers: [successorGroupNumber],
      predecessorPackEntryPks: crossProductPredecessorPks,
      children: phaseoutChildren,
    };

    // Remove predecessor and successor PRODUCT groups from rootGroup.children
    // and replace with the synthetic PHASEOUT group
    rootGroup.children = rootGroup.children.filter((child) => !groupNumbersToRemove.has(child.groupNumber));

    rootGroup.children.push(phaseoutGroup);
  });

  return {
    entryGroups: [rootGroup],
    packageEntries: allPackageEntries,
    products: Array.from(productsMap.values()),
    linearProducts: Array.from(linearProductsMap.values()),
  };
}
